import { useEffect, useState } from "react";

/**
 * Device/viewport utilities for blocking use on small screens (phones).
 * Tablets (typically ≥768px) are allowed; anything smaller is treated as mobile and blocked.
 *
 * Fail-open: we only block when we are confident the viewport is too small. If anything
 * is unclear (no window, invalid width, exception), we allow the app to render so
 * desktop users are never wrongly blocked by an edge case.
 */

/** Minimum viewport width (px) for running the app. Below this = phone, blocked. 768px = tablet breakpoint. */
export const MIN_VIEWPORT_WIDTH_PX = 768;

/**
 * Returns true only when we are confident the viewport is too small (phone).
 * Tablets (≥768px) return false and are allowed.
 * Fail-open: on any doubt (SSR, invalid width, exception), returns false so the app renders.
 */
export function isViewportTooSmallForApp(): boolean {
  try {
    if (typeof window === "undefined") return false;
    const w = window.innerWidth;
    // Treat missing or invalid width as "unknown" — show app (fail open)
    if (w == null || Number.isNaN(Number(w)) || w <= 0) return false;
    return w < MIN_VIEWPORT_WIDTH_PX;
  } catch {
    return false;
  }
}

/**
 * React hook: true when viewport is too small (phone). Updates on resize/orientation change.
 * Uses fail-open logic; on error or invalid state the app is shown.
 */
export function useViewportTooSmall(): boolean {
  const [tooSmall, setTooSmall] = useState(() => isViewportTooSmallForApp());

  useEffect(() => {
    const check = () => setTooSmall(isViewportTooSmallForApp());
    check();
    if (typeof window.addEventListener === "function") {
      window.addEventListener("resize", check);
      window.addEventListener("orientationchange", check);
      return () => {
        window.removeEventListener("resize", check);
        window.removeEventListener("orientationchange", check);
      };
    }
  }, []);

  return tooSmall;
}
